import { Building2, Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAliasDomains } from '@/hooks/useAliasDomains';
import { cn } from '@/lib/utils';
import { isPathWorkspace, workspaceLocation } from '@/services/workspaces';

export function WorkspaceSelector() {
    const {
        selectedWorkspaceId,
        selectedDomain,
        selectedWorkspace,
        selectWorkspace,
        workspaces,
        loading,
        error,
    } = useWorkspace();
    const { isStaff } = useBackofficeAuth();
    const [open, setOpen] = React.useState(false);
    // Canonical is already the selector label, so show only the extra aliases here.
    const { aliases: aliasDomains } = useAliasDomains();

    if (loading) {
        return (
            <div className="workspace-selector workspace-selector--loading flex items-center gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                <span className="text-sm">Loading...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="workspace-selector workspace-selector--error text-sm text-destructive">
                Failed to load workspaces
            </div>
        );
    }

    if (workspaces.length === 0) {
        return (
            <div className="workspace-selector workspace-selector--empty text-sm text-muted-foreground">
                No workspaces available
            </div>
        );
    }

    // `example.com`, or `example.com/fr` for a workspace placed under a path.
    const selectedLocation = selectedWorkspace
        ? workspaceLocation(selectedWorkspace)
        : selectedDomain;
    const selectedName = selectedWorkspace?.name || selectedLocation;
    const hasDistinctSelectedDomain = Boolean(
        selectedLocation && selectedName !== selectedLocation
    );
    const selectedWorkspaceLabel = hasDistinctSelectedDomain
        ? `${selectedName}, ${selectedLocation}`
        : selectedName;

    // Non-admins with only one workspace see static text instead of a dropdown.
    if (!isStaff && workspaces.length === 1) {
        return (
            <div
                className="workspace-selector workspace-selector--static flex items-center gap-2 px-3 py-2"
                title={selectedWorkspaceLabel}
            >
                <Building2 className="workspace-selector__icon size-4 text-muted-foreground" />
                <div className="workspace-selector__static-copy flex min-w-0 flex-col">
                    <span className="workspace-selector__name truncate font-medium">
                        {selectedName}
                    </span>
                    {hasDistinctSelectedDomain && (
                        <span className="workspace-selector__domain truncate text-xs text-muted-foreground">
                            {selectedLocation}
                        </span>
                    )}
                    {aliasDomains.length > 0 && (
                        <span
                            className="workspace-selector__aliases truncate text-xs text-muted-foreground"
                            title={`Also includes: ${aliasDomains.join(', ')}`}
                        >
                            + {aliasDomains.join(', ')}
                        </span>
                    )}
                </div>
            </div>
        );
    }

    // Admins or users with multiple workspaces see a searchable dropdown.
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-label={`Selected workspace: ${selectedWorkspaceLabel}`}
                    className="workspace-selector workspace-selector__trigger h-9 max-w-[280px] gap-2 bg-white px-3 text-[13px] font-medium"
                    title={selectedWorkspaceLabel}
                >
                    <Building2 className="workspace-selector__icon size-3.5 shrink-0 text-muted-foreground" />
                    <span className="workspace-selector__name min-w-0 max-w-[200px] shrink truncate">
                        {selectedName}
                    </span>
                    {hasDistinctSelectedDomain && (
                        <span className="workspace-selector__domain min-w-0 shrink truncate text-xs font-normal text-muted-foreground">
                            {selectedLocation}
                        </span>
                    )}
                    {aliasDomains.length > 0 && (
                        <span
                            className="workspace-selector__aliases shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground"
                            title={`Includes ${aliasDomains.length} more site${aliasDomains.length > 1 ? 's' : ''}: ${aliasDomains.join(', ')}`}
                        >
                            +{aliasDomains.length}
                        </span>
                    )}
                    <ChevronsUpDown className="workspace-selector__chevron size-3.5 shrink-0 text-muted-foreground" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="workspace-selector__dropdown w-[320px] p-0" align="start">
                <Command className="workspace-selector__command">
                    <CommandInput
                        className="workspace-selector__search"
                        placeholder="Search workspaces..."
                    />
                    <CommandList className="workspace-selector__list">
                        <CommandEmpty className="workspace-selector__empty">
                            No workspace found.
                        </CommandEmpty>
                        <CommandGroup className="workspace-selector__group">
                            {workspaces.map((workspace) => (
                                <CommandItem
                                    key={workspace.id}
                                    // The id keeps two same-named workspaces on one host distinct for cmdk.
                                    value={`${workspace.name} ${workspaceLocation(workspace)} ${workspace.id}`}
                                    className="workspace-selector__item"
                                    onSelect={() => {
                                        selectWorkspace(workspace.id);
                                        setOpen(false);
                                    }}
                                >
                                    <Check
                                        className={cn(
                                            'workspace-selector__check size-4 shrink-0',
                                            selectedWorkspaceId === workspace.id
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        )}
                                    />
                                    <div className="workspace-selector__item-content flex min-w-0 flex-1 flex-col py-0.5">
                                        <div className="flex min-w-0 items-baseline gap-2">
                                            <span className="workspace-selector__item-name min-w-0 flex-1 truncate font-medium">
                                                {workspace.name}
                                            </span>
                                            {workspace.status === 'draft' && (
                                                <span className="workspace-selector__item-draft shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                                                    Draft
                                                </span>
                                            )}
                                            {!isPathWorkspace(workspace) && (
                                                <span className="workspace-selector__item-domain max-w-[170px] shrink-0 truncate text-xs text-muted-foreground">
                                                    {workspaceLocation(workspace)}
                                                </span>
                                            )}
                                        </div>
                                        {/* The path is what tells two workspaces on one host apart,
                                            so it gets a full line instead of a clipped corner. */}
                                        {isPathWorkspace(workspace) && (
                                            <span
                                                className="workspace-selector__item-location truncate text-xs text-muted-foreground"
                                                title={workspaceLocation(workspace)}
                                            >
                                                {workspaceLocation(workspace)}
                                            </span>
                                        )}
                                        {workspace.id === selectedWorkspaceId &&
                                            aliasDomains.length > 0 && (
                                                <span className="workspace-selector__item-aliases truncate text-xs text-muted-foreground">
                                                    + {aliasDomains.join(', ')}
                                                </span>
                                            )}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
